/**
 * One instrument, for the integration engineer: exactly what to enter on the instrument, its identity and network
 * settings on our side, module settings, a connection test with one-click re-pin, recent activity, the worklist it
 * sees right now, a printable setup sheet and its event log.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert, Box, Button, FormControlLabel, Grid, Link, Paper, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Aperture, ArrowClockwise, Certificate, ChartLineUp, Files, HardDrives, IdentificationCard, ListChecks, LockSimple, LockSimpleOpen, PlugsConnected, Pulse, PushPin, Trash,
} from "@phosphor-icons/react";
import { AE_PATTERN, DicomNode, fieldErrors, http, isNotFound, ModuleInstall, NodeDetail, NodeDiagnostic, NodePatchBody, WorklistPreviewRow } from "../api";
import { useSession } from "../AuthToken";
import { ConnectionDots, Mono, SectionLabel, StatusChip } from "../ui";
import { caps, fonts, tones } from "../theme";
import { EventLog } from "../console/EventLog";
import { DiagnosticPanel, DiagRun, DIAG_MAX_MS, DIAG_POLL_MS, newer } from "../console/DiagnosticPanel";
import { ModuleSettings } from "../console/ModuleSettings";
import { SetupSheet } from "../console/SetupSheet";
import { ago, BackLink, Confirm, CopyButton, CopyValue, Fact, formatFingerprint, NotYet, Panel, pemFingerprint, Tag } from "../console/common";

type Loaded = { kind: "full"; node: NodeDetail } | { kind: "basic"; node: DicomNode };
type Diag = NodeDiagnostic & { observed?: { fingerprint?: string } };

export default function InstrumentDetail() {
  const { id = "" } = useParams();
  const { token, me } = useSession();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState("");

  const fetchOne = useCallback(async (): Promise<Loaded | string> => {
    const r = await http.get<NodeDetail>(token, `/admin/nodes/${id}`);
    if (r.ok) return { kind: "full", node: r.result };
    if (!isNotFound(r.error)) return r.error;
    const list = await http.get<DicomNode[]>(token, "/admin/nodes");
    const row = list.ok ? list.result.find((n) => n.id === id) : undefined;
    return row ? { kind: "basic", node: row } : list.ok ? "This instrument was not found." : list.error;
  }, [token, id]);

  const load = useCallback(async () => {
    if (!token) return;
    const r = await fetchOne();
    if (typeof r === "string") setError(r);
    else {
      setLoaded(r);
      setError("");
    }
  }, [token, fetchOne]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 15_000);
    return () => clearInterval(iv);
  }, [load]);

  if (me && me.role !== "admin") return <Alert severity="info">The implementer console requires the admin role.</Alert>;
  if (!loaded) return error ? <Stack spacing={2}><BackLink to="/admin?tab=instruments" label="Instruments" /><Alert severity="error">{error}</Alert></Stack> : null;

  const full = loaded.kind === "full" ? loaded.node : null;
  const n = loaded.node;
  const setNode = (d: NodeDetail) => setLoaded({ kind: "full", node: d });
  const setModule = (m: ModuleInstall) => full && setNode({ ...full, modules: full.modules.map((x) => (x.installId === m.installId ? m : x)) });

  return (
    <Stack spacing={3}>
      <BackLink to="/admin?tab=instruments" label="Instruments" />
      {error && <Alert severity="warning" onClose={() => setError("")}>{error}</Alert>}
      <Header loaded={loaded} />
      {!full && (
        <Alert severity="info">
          The full instrument view is not available on this server yet. The connection test and event log are available. Connection details, network settings, modules and the setup sheet appear after the server is updated.
        </Alert>
      )}
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7}>
          <Stack spacing={2.5}>
            {full && <ConnectTo node={full} />}
            {full && <IdentityForm node={full} onSaved={setNode} />}
          </Stack>
        </Grid>
        <Grid item xs={12} lg={full ? 5 : 12}>
          <Stack spacing={2.5}>
            <ConnectionTest loaded={loaded} fetchOne={fetchOne} onLoaded={setLoaded} />
            {full && <Activity node={full} />}
            {full && <Received node={full} />}
          </Stack>
        </Grid>
      </Grid>
      {full && (
        <Stack spacing={1.5}>
          <SectionLabel count={full.modules.length}>Modules</SectionLabel>
          {full.modules.length ? (
            <Grid container spacing={2.5}>
              {full.modules.map((m) => (
                <Grid item xs={12} xl={full.modules.length > 1 ? 6 : 12} key={m.installId}><ModuleSettings install={m} onSaved={setModule} /></Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 2.5, borderRadius: "14px" }}>
              <Typography variant="body2" color="text.secondary">
                No device module is installed. Approve the instrument, or select a module under <Link component={RouterLink} to="/admin?tab=instruments">Admin, Instruments</Link>.
              </Typography>
            </Paper>
          )}
        </Stack>
      )}
      {full && <WorklistPreview nodeId={n.id} />}
      {full && <SetupSheet nodeId={n.id} refreshKey={JSON.stringify([full.connectTo, full.network])} />}
      <EventLog nodeId={n.id} title="Event log for this instrument" />
    </Stack>
  );
}

function Header({ loaded }: { loaded: Loaded }) {
  const n = loaded.node;
  const device = n.device;
  const deviceId = loaded.kind === "full" ? loaded.node.deviceId : loaded.node.device.id;
  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, position: "relative", overflow: "hidden", borderRadius: "14px" }}>
      <Box aria-hidden sx={{ position: "absolute", right: -120, top: -160, width: 360, height: 360, borderRadius: "50%", background: `radial-gradient(circle, ${alpha(tones.ochre, 0.12)}, transparent 65%)`, pointerEvents: "none" }} />
      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ md: "center" }} sx={{ position: "relative" }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="overline" sx={{ color: "primary.main", lineHeight: 1.6 }}>Instrument</Typography>
            <StatusChip status={n.status} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Mono sx={{ fontSize: { xs: 24, md: 30 }, color: "primary.light", wordBreak: "break-all" }}>{n.aet}</Mono>
            <CopyButton value={n.aet} label="Copy AE title" />
          </Stack>
          <Typography variant="body1" sx={{ mt: 0.25 }}>{n.displayName ?? <Box component="span" sx={{ color: "text.secondary" }}>No display name</Box>}</Typography>
          {(n.manufacturer || n.model) && <Typography variant="body2" color="text.secondary">{[n.manufacturer, n.model].filter(Boolean).join(" ")}</Typography>}
        </Box>
        <Stack spacing={0.75} sx={{ borderLeft: { md: 1 }, borderColor: { md: "divider" }, pl: { md: 3 } }}>
          <Typography sx={{ ...caps, fontSize: 9, letterSpacing: "0.18em", color: "text.secondary" }}>Pandia</Typography>
          {deviceId ? (
            <Link component={RouterLink} to={`/admin/edges/${deviceId}`} underline="hover" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: "text.primary" }}>
              <HardDrives size={16} />{device?.name ?? "Open Pandia"}
            </Link>
          ) : (
            <Typography variant="body2">{device?.name ?? "-"}</Typography>
          )}
          {device && <Typography variant="caption" color="text.secondary">{device.tenant?.code ? `${device.tenant.code} / ` : ""}{device.siteCode}{device.lastSeenAt ? ` · Pandia seen ${ago(device.lastSeenAt)}` : ""}</Typography>}
        </Stack>
      </Stack>
    </Paper>
  );
}

function ConnectTo({ node }: { node: NodeDetail }) {
  const c = node.connectTo;
  const big = (label: string, value: string | null, note?: string) => (
    <Grid item xs={6} md={3}>
      <Fact label={label}>
        {value ? <CopyValue value={value} big /> : <Typography sx={{ fontSize: 18, color: "text.secondary" }}>unknown</Typography>}
        {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
      </Fact>
    </Grid>
  );
  return (
    <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "14px", borderColor: alpha(tones.ochre, 0.35), position: "relative", overflow: "hidden" }}>
      <Box aria-hidden sx={{ position: "absolute", left: -80, bottom: -140, width: 280, height: 280, borderRadius: "50%", background: `radial-gradient(circle, ${alpha(tones.ochre, 0.08)}, transparent 70%)`, pointerEvents: "none" }} />
      <Stack spacing={2} sx={{ position: "relative" }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ display: "flex", color: "primary.main" }}><PlugsConnected size={16} /></Box>
          <Typography variant="overline" sx={{ color: "primary.main", lineHeight: 1.6 }}>Connect the instrument to</Typography>
        </Stack>
        <Grid container spacing={2.5}>
          {big("Host / IP", c.host, c.host ? undefined : "The Pandia has not reported a LAN address.")}
          {big("Port", String(c.port))}
          {big("Called AE title", c.aet)}
          <Grid item xs={6} md={3}>
            <Fact label="TLS">
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: c.tls ? tones.live : "text.primary", height: 32 }}>
                {c.tls ? <LockSimple size={20} /> : <LockSimpleOpen size={20} />}
                <Typography sx={{ fontSize: 18 }}>{c.tls ? "Yes" : "No"}</Typography>
              </Stack>
              {c.tls && <Typography variant="caption" color="text.secondary">Install the Pandia certificate on the instrument.</Typography>}
            </Fact>
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary">
          Use these values for both the DICOM Modality Worklist and storage (archive) connections on the instrument. The instrument's calling AE title must be <Mono sx={{ color: "text.primary" }}>{node.aet}</Mono>.
        </Typography>
      </Stack>
    </Paper>
  );
}

function IdentityForm({ node, onSaved }: { node: NodeDetail; onSaved: (n: NodeDetail) => void }) {
  const { token } = useSession();
  const pick = (d: NodeDetail) => ({
    displayName: d.displayName ?? "", aet: d.aet, host: d.network.host ?? "", port: d.network.port ? String(d.network.port) : "",
    dicomTls: d.network.dicomTls, worklistEnabled: d.network.worklistEnabled, storageEnabled: d.network.storageEnabled,
  });
  const [v, setV] = useState(() => pick(node));
  const [base, setBase] = useState(() => pick(node));
  const [pem, setPem] = useState("");
  const [pemFp, setPemFp] = useState<string | null>(null);
  const [clearCert, setClearCert] = useState(false);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const sig = JSON.stringify(pick(node));
  useEffect(() => {
    const next = pick(node);
    setV((cur) => (JSON.stringify(cur) === JSON.stringify(base) ? next : cur));
    setBase(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  useEffect(() => {
    let stop = false;
    if (!pem.trim()) return setPemFp(null);
    pemFingerprint(pem).then((fp) => !stop && setPemFp(fp));
    return () => {
      stop = true;
    };
  }, [pem]);

  const changed = (Object.keys(v) as (keyof typeof v)[]).filter((k) => v[k] !== base[k]);
  const dirty = changed.length > 0 || !!pem.trim() || clearCert;
  const portNum = v.port === "" ? null : Number(v.port);
  const local: Record<string, string> = {
    aet: AE_PATTERN.test(v.aet) ? "" : "Up to 16 characters from A-Z, a-z, 0-9, underscore, hyphen and period.",
    port: portNum === null || (Number.isInteger(portNum) && portNum >= 1 && portNum <= 65535) ? "" : "Enter a port from 1 to 65535, or leave empty.",
    clientCertPem: pem.trim() && pemFp === null ? "Not a PEM certificate. It must begin with -----BEGIN CERTIFICATE-----." : "",
  };
  const invalid = Object.values(local).some(Boolean);
  const err = (k: string) => local[k] || errs[k] || "";

  const save = async () => {
    const body: NodePatchBody = {};
    if (changed.includes("displayName")) body.displayName = v.displayName;
    if (changed.includes("aet")) body.aet = v.aet;
    if (changed.includes("host")) body.host = v.host.trim() || null;
    if (changed.includes("port")) body.port = portNum;
    if (changed.includes("dicomTls")) body.dicomTls = v.dicomTls;
    if (changed.includes("worklistEnabled")) body.worklistEnabled = v.worklistEnabled;
    if (changed.includes("storageEnabled")) body.storageEnabled = v.storageEnabled;
    if (pem.trim()) body.clientCertPem = pem.trim();
    else if (clearCert) body.clientCertPem = null;
    setBusy(true);
    setErrs({});
    const r = await http.patch<NodeDetail>(token, `/admin/nodes/${node.id}`, body);
    setBusy(false);
    if (!r.ok) {
      const fe = fieldErrors(r.error);
      setErrs(fe);
      return setMsg({ tone: "error", text: Object.keys(fe).length ? "Some fields need attention." : r.error });
    }
    setPem("");
    setClearCert(false);
    const next = pick(r.result);
    setBase(next);
    setV(next);
    onSaved(r.result);
    setMsg({ tone: "success", text: changed.includes("aet") ? "Saved. Change the calling AE title on the instrument to match." : "Saved. The Pandia applies this within about 30 s." });
  };

  const mono = { sx: { fontFamily: fonts.mono } };
  const cert = node.network.clientCert;
  return (
    <Panel title="Identity and network" icon={<IdentificationCard size={16} />} action={dirty && <Tag color={tones.ochre}>unsaved</Tag>}>
      <Stack spacing={2.25}>
        {msg && <Alert severity={msg.tone} onClose={() => setMsg(null)}>{msg.text}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Display name" placeholder="Exam room 2 slit lamp" value={v.displayName} onChange={(e) => setV({ ...v, displayName: e.target.value })} error={!!err("displayName")} helperText={err("displayName")} inputProps={{ maxLength: 80 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="AE title (calling)" value={v.aet} onChange={(e) => setV({ ...v, aet: e.target.value })}
              error={!!err("aet")} helperText={err("aet") || (v.aet !== base.aet ? "Must match the calling AE title that the instrument sends." : "The AE title the instrument uses when it calls the edge.")}
              inputProps={{ maxLength: 16, spellCheck: false }} InputProps={mono}
            />
          </Grid>
          <Grid item xs={8} sm={6}>
            <TextField fullWidth size="small" label="Instrument host / IP" value={v.host} onChange={(e) => setV({ ...v, host: e.target.value })} error={!!err("host")} helperText={err("host") || "The instrument's own listener, used for C-ECHO, retrieve and storage commitment."} inputProps={{ spellCheck: false }} InputProps={mono} />
          </Grid>
          <Grid item xs={4} sm={6}>
            <TextField
              fullWidth size="small" label="Port" type="number" value={v.port} onChange={(e) => setV({ ...v, port: e.target.value })}
              error={!!err("port")} helperText={err("port") || (v.port === "" ? "Leave empty if the instrument only initiates connections." : " ")} placeholder="none" InputProps={mono}
            />
          </Grid>
        </Grid>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 3 }}>
          <FormControlLabel control={<Switch checked={v.worklistEnabled} onChange={(e) => setV({ ...v, worklistEnabled: e.target.checked })} />} label={<Box><Typography variant="body2">Worklist</Typography><Typography variant="caption" color="text.secondary">Answer the instrument's worklist queries</Typography></Box>} sx={{ alignItems: "flex-start", m: 0 }} />
          <FormControlLabel control={<Switch checked={v.storageEnabled} onChange={(e) => setV({ ...v, storageEnabled: e.target.checked })} />} label={<Box><Typography variant="body2">Storage</Typography><Typography variant="caption" color="text.secondary">Accept images the instrument sends</Typography></Box>} sx={{ alignItems: "flex-start", m: 0 }} />
        </Stack>
        <Box sx={{ p: 2, borderRadius: "12px", border: 1, borderColor: v.dicomTls ? alpha(tones.live, 0.3) : "divider", bgcolor: v.dicomTls ? alpha(tones.live, 0.04) : "transparent", transition: "all 0.3s" }}>
          <FormControlLabel
            control={<Switch color="secondary" checked={v.dicomTls} onChange={(e) => setV({ ...v, dicomTls: e.target.checked })} />}
            label={<Box><Typography>DICOM TLS</Typography><Typography variant="caption" color="text.secondary">The instrument connects to the Pandia's DICOM TLS port instead of the plain port. The edge's DICOM TLS listener must be turned on.</Typography></Box>}
            sx={{ alignItems: "flex-start", m: 0 }}
          />
          {v.dicomTls && (
            <Stack spacing={1.25} sx={{ mt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Certificate size={16} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Client certificate (mutual TLS)</Typography>
              </Stack>
              {cert && !clearCert && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ p: 1.25, borderRadius: "10px", bgcolor: "rgba(0,0,0,0.25)", border: 1, borderColor: "divider" }}>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Currently trusted: <Mono>{cert.subject}</Mono></Typography>
                    <CopyValue value={cert.fingerprint} display={formatFingerprint(cert.fingerprint)} />
                  </Box>
                  <Button size="small" color="error" startIcon={<Trash size={14} />} onClick={() => setClearCert(true)}>Remove</Button>
                </Stack>
              )}
              {clearCert && <Alert severity="warning" action={<Button size="small" color="inherit" onClick={() => setClearCert(false)}>Undo</Button>}>The trusted certificate will be removed when you save.</Alert>}
              <TextField
                multiline minRows={3} maxRows={10} size="small" value={pem} onChange={(e) => setPem(e.target.value)}
                placeholder={"-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"}
                label={cert && !clearCert ? "Replace with PEM" : "Paste the instrument's certificate (PEM)"}
                error={!!err("clientCertPem")}
                helperText={err("clientCertPem") || "Only needed when the Pandia requires client certificates. FORUM needs one; HEYEX refuses them."}
                InputProps={{ sx: { fontFamily: fonts.mono, fontSize: 12 } }}
              />
              {pemFp && (
                <Box sx={{ pl: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">SHA-256 of the pasted certificate</Typography>
                  <CopyValue value={pemFp} display={pemFp} />
                </Box>
              )}
            </Stack>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={save} disabled={!dirty || invalid || busy}>Save</Button>
          {dirty && <Button color="inherit" onClick={() => { setV(base); setPem(""); setClearCert(false); setErrs({}); }}>Discard</Button>}
        </Stack>
      </Stack>
    </Panel>
  );
}

const tlsFailed = (d: Diag | null | undefined) =>
  !!d && !!d.observed?.fingerprint && d.steps.some((s) => s.status === "fail" && /tls|cert|fingerprint|pin/i.test(`${s.key} ${s.label}`));

function ConnectionTest({ loaded, fetchOne, onLoaded }: { loaded: Loaded; fetchOne: () => Promise<Loaded | string>; onLoaded: (l: Loaded) => void }) {
  const { token } = useSession();
  const n = loaded.node;
  const [run, setRun] = useState<DiagRun | undefined>();
  const [echoMsg, setEchoMsg] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [pinMsg, setPinMsg] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const runRef = useRef(run);
  runRef.current = run;
  const last = (n.lastDiagnostic ?? null) as Diag | null;
  const port = loaded.kind === "full" ? loaded.node.network.port : loaded.node.port;

  const test = async () => {
    const local = new Date().toISOString();
    setPinMsg(null);
    setRun({ phase: "running", requestedAt: local, started: Date.now() });
    const r = await http.post<{ id: string; diagnoseRequestedAt: string }>(token, `/admin/nodes/${n.id}/diagnose`);
    if (!r.ok) return setRun((x) => x && { ...x, phase: isNotFound(r.error) ? "unavailable" : "error", message: r.error });
    setRun((x) => x && { ...x, requestedAt: r.result?.diagnoseRequestedAt ?? local });
  };

  const running = run?.phase === "running";
  useEffect(() => {
    if (!running || !token) return;
    const iv = setInterval(async () => {
      const cur = runRef.current;
      if (!cur || cur.phase !== "running") return;
      const l = await fetchOne();
      if (typeof l === "string") return;
      const d = l.node.lastDiagnostic;
      if (newer(d, cur.requestedAt)) {
        setRun({ ...cur, phase: "done", result: d! });
        onLoaded(l);
      } else if (Date.now() - cur.started > DIAG_MAX_MS) setRun({ ...cur, phase: "timeout" });
    }, DIAG_POLL_MS);
    return () => clearInterval(iv);
  }, [running, token, fetchOne, onLoaded]);

  const echo = async () => {
    const r = await http.post(token, `/admin/nodes/${n.id}/echo`);
    setEchoMsg(r.ok ? "C-ECHO requested. The result appears here when the Pandia reports." : r.error);
  };

  const shown = (run?.result ?? (run && run.phase !== "timeout" ? null : last)) as Diag | null;
  const observed = shown?.observed?.fingerprint;
  const pin = async () => {
    if (!observed) return;
    setPinBusy(true);
    const r = await http.post<NodeDetail>(token, `/admin/nodes/${n.id}/pin-fingerprint`, { fingerprint: observed });
    setPinBusy(false);
    setPinOpen(false);
    if (!r.ok) return setPinMsg({ tone: "error", text: isNotFound(r.error) ? "Re-pinning is not available on this server yet." : r.error });
    if (r.result?.id) onLoaded({ kind: "full", node: r.result });
    setPinMsg({ tone: "success", text: "Pinned. Run the connection test again to confirm that the live stream connects." });
  };

  const footer = (tlsFailed(shown) || pinMsg) && (
    <Stack spacing={1.25}>
      {tlsFailed(shown) && observed && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ p: 1.5, borderRadius: "10px", border: `1px solid ${alpha(tones.warn, 0.35)}`, bgcolor: alpha(tones.warn, 0.06) }}>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="body2">The instrument presented a different certificate from the one pinned. If the certificate was regenerated on purpose, trust the new one.</Typography>
            <Typography variant="caption" color="text.secondary">Presented now: </Typography>
            <Mono sx={{ fontSize: 11.5, wordBreak: "break-all" }}>{formatFingerprint(observed)}</Mono>
          </Box>
          <Button variant="outlined" startIcon={<PushPin size={15} />} onClick={() => setPinOpen(true)} sx={{ flexShrink: 0 }}>Re-pin to the certificate presented now</Button>
        </Stack>
      )}
      {pinMsg && <Alert severity={pinMsg.tone} onClose={() => setPinMsg(null)}>{pinMsg.text}</Alert>}
    </Stack>
  );

  const lastEcho = n.lastEcho;
  return (
    <Panel
      title="Connection test"
      icon={<Pulse size={16} />}
      action={
        <Stack direction="row" spacing={0.5}>
          {port ? <Button size="small" color="inherit" onClick={echo}>C-ECHO</Button> : <Tooltip title="This instrument only initiates connections. It has no DICOM listener, so there is nothing to echo."><Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center", mr: 1 }}>initiates only</Typography></Tooltip>}
          <Button size="small" variant="contained" startIcon={<Pulse size={14} />} onClick={test} disabled={running || n.status === "rejected"}>{running ? "Testing" : "Test connection"}</Button>
        </Stack>
      }
    >
      <Stack spacing={1.5}>
        {(n.echoRequestedAt || lastEcho) && (
          <Stack direction="row" spacing={1} alignItems="center">
            {n.echoRequestedAt ? <ConnectionDots state="connecting" dots={4} size={3} label="echo pending" /> : lastEcho && (
              <Tooltip title={lastEcho.detail ?? ""}><span><StatusChip status={lastEcho.ok ? "ok" : "failed"} label={`echo ${lastEcho.ok ? "ok" : "failed"} · ${ago(lastEcho.at)}`} /></span></Tooltip>
            )}
          </Stack>
        )}
        {echoMsg && <Alert severity="info" onClose={() => setEchoMsg("")}>{echoMsg}</Alert>}
        {run || last ? (
          <DiagnosticPanel aet={n.aet} run={run} last={last} footer={footer} />
        ) : (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
            <ConnectionDots state="idle" from={<HardDrives size={18} />} to={<Aperture size={18} />} dots={8} />
            <Typography variant="body2" color="text.secondary">Not tested yet. The Pandia checks reachability, the DICOM association, TLS and the live stream from the site.</Typography>
          </Stack>
        )}
      </Stack>
      <Confirm open={pinOpen} title="Re-pin certificate" confirm="Trust this certificate" busy={pinBusy} onClose={() => setPinOpen(false)} onConfirm={pin}>
        <Typography variant="body2">Jupiter will trust the certificate that the instrument presented in the last test:</Typography>
        <Mono sx={{ wordBreak: "break-all", color: "primary.light" }}>{observed ? formatFingerprint(observed) : ""}</Mono>
        <Alert severity="warning">Only do this if you know the instrument's certificate changed (after a reset, firmware update or regeneration). An unexpected change can mean that another device is answering at that address.</Alert>
      </Confirm>
    </Panel>
  );
}

function Activity({ node }: { node: NodeDetail }) {
  const a = node.activity;
  const storeOk = a.lastStoreStatus ? /^(ok|success|stored|0000)$/i.test(a.lastStoreStatus) : null;
  return (
    <Panel title="Activity" icon={<ChartLineUp size={16} />}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Fact label="Last worklist query">
            <Typography variant="body2">{ago(a.lastWorklistQueryAt)}</Typography>
            {a.lastWorklistQueryAt && <Typography variant="caption" color="text.secondary">{new Date(a.lastWorklistQueryAt).toLocaleString()}</Typography>}
          </Fact>
        </Grid>
        <Grid item xs={6}>
          <Fact label="Stores today">
            <Typography sx={{ fontFamily: fonts.display, fontWeight: 200, fontSize: 28, lineHeight: 1.1 }}>{a.storesToday}</Typography>
          </Fact>
        </Grid>
        <Grid item xs={12}>
          <Fact label="Last store">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2">{ago(a.lastStoreAt)}</Typography>
              {a.lastStoreStatus && <StatusChip status={storeOk ? "ok" : "failed"} tone={storeOk ? "ok" : "warn"} label={a.lastStoreStatus} />}
            </Stack>
            {a.lastStoreAt && <Typography variant="caption" color="text.secondary">{new Date(a.lastStoreAt).toLocaleString()}</Typography>}
          </Fact>
        </Grid>
      </Grid>
    </Panel>
  );
}

// What this instrument actually sends, by object type, and how often it records the eye itself. Evidence for the
// implementer (and for any "DICOM compliant" claim): a low eye figure means staff must assign eyes in Jupiter.
function Received({ node }: { node: NodeDetail }) {
  const rows = node.received;
  if (!rows) return null;
  const pct = (n: number, d: number) => (d ? `${Math.round((n / d) * 100)}%` : "n/a");
  return (
    <Panel title="Objects received" icon={<Files size={16} />}>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Nothing received from this instrument yet.</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Object type</TableCell><TableCell align="right">Received</TableCell><TableCell align="right">Eye recorded by instrument</TableCell><TableCell align="right">Eye assigned in Jupiter</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.sopClassUid}>
                  <TableCell>
                    <Typography variant="body2">{r.name}</Typography>
                    {r.lastReceivedAt && <Typography variant="caption" color="text.secondary">Last {ago(r.lastReceivedAt)}</Typography>}
                  </TableCell>
                  <TableCell align="right">{r.objects}</TableCell>
                  <TableCell align="right" sx={{ color: r.eyeFromInstrument < r.objects ? tones.ochre : undefined }}>{pct(r.eyeFromInstrument, r.objects)}</TableCell>
                  <TableCell align="right">{r.eyeAssigned}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
        The eye comes from Image Laterality (0020,0062) or series Laterality (0020,0060) in each object. Where the instrument does not record it, staff assign it on the order page.
      </Typography>
    </Panel>
  );
}

function WorklistPreview({ nodeId }: { nodeId: string }) {
  const { token } = useSession();
  const [rows, setRows] = useState<WorklistPreviewRow[] | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing" | "error">("loading");
  const [error, setError] = useState("");
  const [at, setAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    const r = await http.get<WorklistPreviewRow[]>(token, `/admin/nodes/${nodeId}/worklist-preview`);
    if (r.ok) {
      setRows(r.result ?? []);
      setState("ready");
      setAt(new Date());
    } else {
      setState(isNotFound(r.error) ? "missing" : "error");
      setError(r.error);
    }
  }, [token, nodeId]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <Panel
      title="Worklist preview"
      icon={<ListChecks size={16} />}
      action={state !== "missing" && (
        <Stack direction="row" spacing={1} alignItems="center">
          {at && <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>as of {at.toLocaleTimeString()}</Typography>}
          <Button size="small" color="inherit" startIcon={<ArrowClockwise size={14} />} onClick={load}>Refresh</Button>
        </Stack>
      )}
    >
      {state === "missing" ? (
        <NotYet>The worklist preview is not available on this server yet.</NotYet>
      ) : (
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">Worklist entries this instrument receives when it queries the worklist now.</Typography>
          {state === "error" && <Alert severity="error">{error}</Alert>}
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: "12px", overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow><TableCell>Scheduled</TableCell><TableCell>Patient</TableCell><TableCell>MRN</TableCell><TableCell>Accession</TableCell><TableCell>Procedure</TableCell><TableCell>Station AE title</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {(rows ?? []).map((r) => (
                  <TableRow key={`${r.accession}-${r.stationAeTitle}`}>
                    <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>{new Date(r.scheduledAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</TableCell>
                    <TableCell>{r.patientName}</TableCell>
                    <TableCell><Mono>{r.mrn}</Mono></TableCell>
                    <TableCell><Mono sx={{ color: "primary.light" }}>{r.accession}</Mono></TableCell>
                    <TableCell>{r.procedure}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Mono>{r.stationAeTitle}</Mono>
                        {r.sharedWithAll && <Tag color={tones.live}>shared with all instruments</Tag>}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {state === "ready" && !rows?.length && (
                  <TableRow><TableCell colSpan={6} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>Nothing scheduled for this instrument right now.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      )}
    </Panel>
  );
}
